/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { SpecialRequest } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { Divider, Group, Radio, rem, Stack, Text, Title } from '@mantine/core';
import { Dot } from 'lucide-react';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import styles from '@/components/RadioButton.module.css';
import { ROUTES } from '@/config/router';
import {
  insuranceOptionsBike,
  insuranceOptionsTruck,
  type InsuranceOption,
} from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { findInsuranceInRequests } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';
import { InsurancePackageType } from '@/types/booking';

type InsuranceOptionProps = {
  close: () => void;
  request: SpecialRequest;
  handleChangeRequestParams: (
    id: string,
    value: string | number | undefined | boolean
  ) => void;
};

const InsuranceOptions = ({
  close,
  handleChangeRequestParams,
  request,
}: InsuranceOptionProps) => {
  const isMobile = useIsMobile();
  const pathname = usePathname();

  const { estimatedData, setInsuranceValue, insuranceValue, selectedService } =
    useBookingStore((state) => state);

  const paths = useLocationStore.use.paths();

  const highestItemValue = useMemo(() => {
    return Math.max(...paths.slice(1).map((path) => path.item_value ?? 0));
  }, [paths]);

  const getDefaultInsuranceValue = (value: number): InsurancePackageType => {
    if (value <= 5000000) return InsurancePackageType.Basic;
    if (value <= 10000000) return InsurancePackageType.Advanced;
    if (value <= 30000000) return InsurancePackageType.Vip;
    return InsurancePackageType.None;
  };

  const insuranceOptions = useMemo(() => {
    if (pathname === ROUTES.truck) {
      return insuranceOptionsTruck;
    }
    return insuranceOptionsBike;
  }, [pathname]);

  const [value, setValue] = useState<InsurancePackageType>(
    getDefaultInsuranceValue(highestItemValue)
  );
  const [updatedOptions, setUpdatedOptions] =
    useState<InsuranceOption[]>(insuranceOptions);

  const arrItemValue = paths.slice(1).map((path) => path.item_value ?? 0);

  const insuranceRequest = useMemo(() => {
    return selectedService ? findInsuranceInRequests(selectedService) : null;
  }, [selectedService]);

  const matchOptionByPriceBlock = () => {
    const priceBlocks = insuranceRequest?.price_blocks;

    if (!priceBlocks) return;

    const newOptions = insuranceOptions.map((option, index) => {
      const matchedBlock = priceBlocks[index];

      return {
        ...option,
        ...(matchedBlock ? matchedBlock : {}),
      };
    });

    setUpdatedOptions(newOptions);
  };

  useEffect(() => {
    matchOptionByPriceBlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  const onSelectOptionInsurance = (value: string) => {
    setValue(value as InsurancePackageType);
    setInsuranceValue(value as InsurancePackageType);
    if (value === InsurancePackageType.None) {
      handleChangeRequestParams(request._id, undefined);
    } else {
      handleChangeRequestParams(request._id, true);
    }
    close();
  };

  const filteredOptions = useMemo(() => {
    return updatedOptions.filter((option) => {
      if (option.id === InsurancePackageType.None) return true;

      if (
        option.from !== undefined &&
        option.to !== undefined &&
        option.from >= 0 &&
        option.to >= 0
      ) {
        return highestItemValue >= option.from && highestItemValue < option.to;
      }

      return false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedOptions, highestItemValue]);

  const calculateCompensation = (arrItemValue: number[]) => {
    const matchedOptions = arrItemValue.flatMap((itemValue) =>
      insuranceOptions.filter(
        (option) =>
          option.from !== undefined &&
          option.to !== undefined &&
          itemValue >= option.from &&
          itemValue < option.to
      )
    );

    if (!matchedOptions.length) {
      console.error('error matchedOptions not found!');
    }

    const sumCompensation = matchedOptions.reduce(
      (sum, option) => sum + (option.compensation || 0),
      0
    );
    const sumPrice = matchedOptions.reduce(
      (sum, option) => sum + (option.price || 0),
      0
    );

    return {
      matchedOptions: matchedOptions.map((option) => option.id),
      sumCompensation,
      sumPrice,
    };
  };

  const totalCompensation = useMemo(() => {
    return calculateCompensation(arrItemValue).sumCompensation;
  }, [arrItemValue]);

  const totalPrice = useMemo(() => {
    return calculateCompensation(arrItemValue).sumPrice;
  }, [arrItemValue]);

  const pricePerPoint = useMemo(() => {
    const request = estimatedData?.data?.requests;

    if (!request) return null;

    return formatCurrency(insuranceRequest?.price || totalPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedData?.data?.requests, totalPrice]);

  useEffect(() => {
    if (insuranceValue) {
      setValue(insuranceValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions]);

  useEffect(() => {
    // Auto select the first option when user change the item_value range
    if (
      filteredOptions.length > 0 &&
      insuranceValue !== InsurancePackageType.None
    ) {
      const firstOptionId = filteredOptions[0].id as InsurancePackageType;

      setInsuranceValue(firstOptionId);
      setValue(firstOptionId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions, insuranceValue]);

  const cards = filteredOptions.map((item, index) => (
    <React.Fragment key={item.name}>
      {index !== 0 && <Divider color="neutral.2" />}
      <Radio.Card
        radius="md"
        value={item.id}
        px={rem(8)}
        py={rem(16)}
        styles={{
          card: {
            zIndex: 10,
            border: 'none',
            width: isMobile ? '100%' : 343,
            maxWidth: '100%',
          },
        }}
      >
        <Group wrap="nowrap" justify="space-between" align="center">
          <Stack gap={0}>
            <Group wrap="nowrap" justify="flex-start" gap={1}>
              <Title c="neutral.7" order={5}>
                {item.name}
              </Title>
              {item.id !== InsurancePackageType.None && pricePerPoint ? (
                <>
                  <Dot className="size-4" color="#DDE1E6" />

                  <Text fw={600} c="secondary.5">
                    {pricePerPoint}/{paths.length - 1} điểm giao
                  </Text>
                </>
              ) : null}
            </Group>
            <Text c="neutral.5" fz={12}>
              {item.id === InsurancePackageType.None
                ? item.description
                : `Mức bồi thường tối đa lên đến ${formatCurrency(totalCompensation)}`}
            </Text>
          </Stack>
          <Radio.Indicator
            variant="outline"
            classNames={{ indicator: styles.radioBtn }}
          />
        </Group>
      </Radio.Card>
    </React.Fragment>
  ));

  return (
    <div className="size-full rounded-b-xl bg-white">
      <Radio.Group
        value={value}
        onChange={(e) => {
          onSelectOptionInsurance(e);
        }}
      >
        <Stack gap={0} px={rem(8)}>
          {cards}
        </Stack>
      </Radio.Group>
    </div>
  );
};

export default InsuranceOptions;
